import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { InsightCardErrorBoundary } from "../components/insights/InsightErrorBoundary";
import { useInsightSearch } from "../hooks/useInsights";
import supabaseClient from "../lib/supabaseClient";
import {
  useGenerationHistory,
  GenerationHistoryItem,
  GenerationType,
  GenerationStatus,
  GenerationHistoryFilters,
} from "../hooks/useGenerationHistory";
import {
  integrationsService,
  UserIntegration,
} from "../services/integrationsService";
import { Insight } from "../../../api/src/types/insights";
import InsightListCard from "../components/insights/InsightListCard";
import { getIntegrationLabel } from "../utils/integrationLabels";
import { cn } from "../lib/utils";
import { formatDateTime, formatDateRange } from "../utils/dateFormatters";
import {
  Check,
  Plus,
  Sparkles,
  Calendar as CalendarIcon,
  ArrowRight,
  Search,
  Filter,
  X,
  Mail,
  Lightbulb,
  RefreshCw,
  Trash2,
  Eye,
  Copy,
  ChevronDown,
  Clock,
  CheckCircle,
  AlertCircle,
  Send,
  FileEdit,
  Loader2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import PageHeader from "../components/common/PageHeader";

// Import styles
import "../styles/insights.css";

const Insights: React.FC = () => {
  const { user } = useAuth();

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Please log in to view insights.</p>
      </div>
    );
  }

  return (
    <InsightCardErrorBoundary>
      <TabbedInsights userId={user.id} />
    </InsightCardErrorBoundary>
  );
};

export default Insights;

// --- Inline Generator ---
interface InlineInsightGeneratorProps {
  userId: string;
  onCreateUpdate?: (insights: Insight[]) => void;
}

const InlineInsightGenerator: React.FC<InlineInsightGeneratorProps> = ({
  onCreateUpdate,
}) => {
  const navigate = useNavigate();
  const [isGenerating, setIsGenerating] = useState(false);

  const [integrations, setIntegrations] = useState<UserIntegration[]>([]);
  const [loadingIntegrations, setLoadingIntegrations] = useState(false);
  const [question, setQuestion] = useState("");
  const [selectedIntegrationIds, setSelectedIntegrationIds] = useState<
    string[]
  >([]);
  const [optionsByProvider, setOptionsByProvider] = useState<
    Record<
      string,
      | {
          type: "none";
          kinds?: Array<{ id: string; name: string; description?: string }>;
        }
      | {
          type: "list";
          label: string;
          items: Array<{ id: string; name: string }>;
          kinds?: Array<{ id: string; name: string; description?: string }>;
        }
    >
  >({});
  const [selectionByIntegrationId, setSelectionByIntegrationId] = useState<
    Record<
      string,
      { selectedIds: Record<string, boolean>; kinds: Record<string, boolean> }
    >
  >({});
  const [dateStart, setDateStart] = useState<string>("");
  const [dateEnd, setDateEnd] = useState<string>("");
  const [preset, setPreset] = useState<
    "last_week" | "last_month" | "last_quarter" | "custom" | ""
  >("");
  const [progress, setProgress] = useState<{
    step: string;
    percent: number;
    etaMs: number;
  } | null>(null);
  const [genError, setGenError] = useState<string | null>(null);
  const [generatedInsights, setGeneratedInsights] = useState<Insight[] | null>(
    null
  );
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expandedGenerated, setExpandedGenerated] = useState<Set<string>>(
    new Set()
  );

  const normalizeProviderKey = (raw: string): string => {
    const key = (raw || "").toLowerCase();
    const aliases: Record<string, string> = {
      gsheets: "google-sheets",
      "google sheets": "google-sheets",
      "g sheets": "google-sheets",
      "google-sheets": "google-sheets",
      ga: "google-analytics",
      "google-analytics": "google-analytics",
      sheets: "google-sheets",
    };
    return aliases[key] || key.replace(/\s+/g, "_");
  };

  const getIntegrationIconSrc = (provider: string): string | null => {
    const map: Record<string, string> = {
      slack: "/assets/integrations/slack.svg",
      github: "/assets/integrations/github-white.svg",
      jira: "/assets/integrations/jira.svg",
      google: "/assets/integrations/google.svg",
      gitlab: "/assets/integrations/gitlab.svg",
      asana: "/assets/integrations/asana.png",
      trello: "/assets/integrations/trello.svg",
      notion: "/assets/integrations/notion.png",
      mixpanel: "/assets/integrations/mixpanel.svg",
      monday: "/assets/integrations/monday.svg",
      clickup: "/assets/integrations/clickup.svg",
      "google-sheets": "/assets/integrations/sheets.png",
      sheets: "/assets/integrations/sheets.png",
      drive: "/assets/integrations/drive.png",
      docs: "/assets/integrations/docs.png",
      stripe: "/assets/integrations/stripe.png",
      hubspot: "/assets/integrations/hubspot.svg",
      intercom: "/assets/integrations/intercom.svg",
      salesforce: "/assets/integrations/salesforce.svg",
      linear: "/assets/integrations/linear-white.png",
      zendesk: "/assets/integrations/zendesk.svg",
      typeform: "/assets/integrations/typeform.svg",
      twitter: "/assets/integrations/twitter.svg",
      google_contacts: "/assets/integrations/google-contacts.svg",
    };
    const key = normalizeProviderKey(provider);
    return map[key] || null;
  };

  useEffect(() => {
    const load = async () => {
      setLoadingIntegrations(true);
      try {
        const list = await integrationsService.getUserIntegrations();
        setIntegrations(list.filter((i) => i.integration_status === "active"));
        // Background fetch options for each provider
        const uniqueProviders = Array.from(
          new Set(
            list
              .filter((i) => i.integration_status === "active")
              .map((i) => (i.provider || "").toLowerCase())
          )
        );
        const results: Array<[string, any]> = await Promise.all(
          uniqueProviders.map(async (p): Promise<[string, any]> => {
            try {
              const opts = await integrationsService.getProviderOptions(p);
              return [p, opts] as [string, any];
            } catch {
              return [p, { type: "none" }] as [string, any];
            }
          })
        );
        const map: any = {};
        results.forEach(([p, o]) => {
          map[p] = o;
        });
        setOptionsByProvider(map);

        // Load saved data selections for each integration
        const savedSelections: Record<string, { selectedIds: Record<string, boolean>; kinds: Record<string, boolean> }> = {};
        for (const integration of list.filter((i) => i.integration_status === "active")) {
          try {
            const settings = await integrationsService.getIntegrationSettings(integration.provider);
            if (settings?.selectedRepositories || settings?.selectedKinds) {
              const selectedIds: Record<string, boolean> = {};
              (settings.selectedRepositories || []).forEach((id: string) => {
                selectedIds[id] = true;
              });
              const kinds: Record<string, boolean> = {};
              (settings.selectedKinds || []).forEach((k: string) => {
                kinds[k] = true;
              });
              savedSelections[integration.id] = { selectedIds, kinds };
              console.log(`✅ Loaded saved selections for ${integration.provider}:`, savedSelections[integration.id]);
            }
          } catch (err) {
            console.warn(`Failed to load settings for ${integration.provider}:`, err);
          }
        }
        setSelectionByIntegrationId(savedSelections);
      } finally {
        setLoadingIntegrations(false);
      }
    };
    load();
  }, []);

  const quickSelect = (
    which: "last_week" | "last_month" | "last_quarter" | "custom"
  ) => {
    const end = new Date();
    let start = new Date();
    if (which === "last_week") {
      start = new Date(end.getTime() - 7 * 24 * 60 * 60 * 1000);
    } else if (which === "last_month") {
      start = new Date(end.getTime() - 30 * 24 * 60 * 60 * 1000);
    } else if (which === "last_quarter") {
      start = new Date(end.getTime() - 90 * 24 * 60 * 60 * 1000);
    }
    setPreset(which);
    if (which !== "custom") {
      setDateStart(start.toISOString().slice(0, 10));
      setDateEnd(end.toISOString().slice(0, 10));
    }
  };

  const toggleIntegration = (id: string) => {
    setSelectedIntegrationIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const resetForm = () => {
    setGeneratedInsights(null);
    setQuestion("");
    setSelectedIntegrationIds([]);
    setSelectionByIntegrationId({});
    setDateStart("");
    setDateEnd("");
    setPreset("");
    setProgress(null);
    setGenError(null);
    setSelected(new Set());
    setExpandedGenerated(new Set());
  };

  const handleGenerate = async () => {
    if (selectedIntegrationIds.length === 0) return;
    setGenError(null);
    setIsGenerating(true);

    const payload: any = {
      type: "comparison" as const,
      category: "operational" as const,
      data_sources: selectedIntegrationIds,
      integrationIds: selectedIntegrationIds,
      timeRange:
        dateStart && dateEnd
          ? {
              start: new Date(dateStart).toISOString(),
              end: new Date(dateEnd).toISOString(),
            }
          : undefined,
      dataSelections: (() => {
        const obj: Record<
          string,
          { selectedIds?: string[]; kinds?: string[] }
        > = {};
        for (const intId of selectedIntegrationIds) {
          const int = integrations.find((i) => i.id === intId);
          const provider = (int?.provider || "").toLowerCase();
          const sel = selectionByIntegrationId[intId];
          const providerOpts = optionsByProvider[provider];
          if (!sel && (!providerOpts || providerOpts.type === "none")) continue;
          const ids = sel
            ? Object.keys(sel.selectedIds).filter((k) => sel.selectedIds[k])
            : [];
          const kinds = sel
            ? Object.keys(sel.kinds).filter((k) => sel.kinds[k])
            : Array.isArray((providerOpts as any)?.kinds)
            ? []
            : [];
          if ((ids && ids.length > 0) || (kinds && kinds.length > 0)) {
            obj[intId] = {};
            if (ids.length > 0) obj[intId].selectedIds = ids;
            if (kinds.length > 0) obj[intId].kinds = kinds;
          }
        }
        return Object.keys(obj).length > 0 ? obj : undefined;
      })(),
    };

    if (question.trim()) {
      payload.question = question.trim();
    }

    // Staged progress steps (approximate) — drive UI while SSE stream is open
    const steps = [
      { step: "Queueing your insight job...", percent: 10, duration: 1100 },
      { step: "Analyzing your question and context...", percent: 28, duration: 1900 },
      { step: "Fetching data from selected integrations...", percent: 52, duration: 2600 },
      { step: "Processing metrics and comparisons...", percent: 75, duration: 2600 },
      { step: "Compiling narrative and takeaways...", percent: 90, duration: 1900 },
    ];

    let streamDone = false;

    // Drive the progress UI while the SSE stream runs
    (async () => {
      for (let i = 0; i < steps.length; i++) {
        if (streamDone) break;
        const remaining = steps
          .slice(i + 1)
          .reduce((s, x) => s + x.duration, 0);
        setProgress({
          step: steps[i].step,
          percent: steps[i].percent,
          etaMs: remaining,
        });
        const start = Date.now();
        while (Date.now() - start < steps[i].duration) {
          if (streamDone) break;
          await new Promise((r) => setTimeout(r, 120));
        }
      }
      if (!streamDone) {
        setProgress({ step: "Finalizing...", percent: 95, etaMs: 0 });
      }
    })();

    const API_BASE_URL =
      process.env.REACT_APP_API_URL ||
      (window.location.hostname === "localhost"
        ? "http://localhost:4000"
        : "https://api.sendupdates.ai");

    try {
      // Get auth token
      const { data: sessionData } = await supabaseClient.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error("Authentication required");

      // 500s timeout covering the entire SSE stream
      const controller = new AbortController();
      const streamTimeout = setTimeout(() => controller.abort(), 500000);

      const response = await fetch(
        `${API_BASE_URL}/insights/generate-stream`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
          signal: controller.signal,
        }
      );

      if (!response.ok) {
        clearTimeout(streamTimeout);
        const errBody = await response.text().catch(() => "");
        throw new Error(
          errBody || `HTTP error! status: ${response.status}`
        );
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      if (!reader) {
        clearTimeout(streamTimeout);
        throw new Error("No response body");
      }

      let buffer = "";
      let done = false;
      let receivedInsights: Insight[] = [];

      while (!done) {
        const result = await reader.read();
        done = result.done;
        if (done) break;

        buffer += decoder.decode(result.value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (let i = 0; i < lines.length; i++) {
          const line = lines[i].trim();
          if (line.startsWith("event:")) {
            const eventType = line.substring(6).trim();
            const dataLine = lines[i + 1];
            if (dataLine?.startsWith("data:")) {
              const jsonData = dataLine.substring(5).trim();
              try {
                const eventData = JSON.parse(jsonData);
                if (eventType === "complete") {
                  if (eventData.data?.insights) {
                    receivedInsights = eventData.data.insights;
                  } else if (eventData.data?.insight) {
                    receivedInsights = [eventData.data.insight];
                  }
                } else if (eventType === "error") {
                  throw new Error(
                    eventData.error || "Server error during generation"
                  );
                }
              } catch (parseError) {
                if (
                  parseError instanceof Error &&
                  parseError.message !== "Server error during generation"
                ) {
                  console.error("Failed to parse SSE data:", parseError);
                } else {
                  throw parseError;
                }
              }
              i++; // skip the data line we already processed
            }
          }
        }
      }

      // Stream fully consumed — clear the timeout
      clearTimeout(streamTimeout);
      streamDone = true;

      if (receivedInsights.length > 0) {
        setProgress({
          step: "Insights generated! Rendering results...",
          percent: 100,
          etaMs: 0,
        });
        setTimeout(() => setProgress(null), 800);
        setGeneratedInsights(receivedInsights);
        setSelected(new Set());
      } else {
        setProgress(null);
        setGenError("No insights were generated. Please try again.");
      }
    } catch (err: any) {
      streamDone = true;
      setProgress(null);
      setGenError(err?.message || "Failed to generate insight");
    } finally {
      setIsGenerating(false);
    }
  };

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-6">
      {isGenerating || progress ? (
        <div className="su-section">
          <div className="su-insights-progress">
            <div className="flex items-start gap-3">
              <div className="mt-0.5">
                <div className="animate-spin h-5 w-5 border-b-2 border-purple-500" />
              </div>
              <div className="flex-1">
                <div className="su-insights-section-title" style={{ marginBottom: 0 }}>
                  Generating Insights
                </div>
                <div className="su-insights-progress-text">
                  {progress?.step || "Working on your request..."}
                </div>
                {progress && (
                  <div className="mt-3">
                    <div className="flex justify-between text-xs text-gray-300 mb-1">
                      <span>Progress</span>
                      <span>{progress.percent}%</span>
                    </div>
                    <div className="su-insights-progress-bar">
                      <div
                        className="su-insights-progress-fill"
                        style={{ width: `${progress.percent}%` }}
                      />
                    </div>
                    <div className="su-insights-progress-eta">
                      You can leave this page — generation continues in the
                      background. Check &quot;Automated Insights&quot; in ~3–5
                      minutes.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : generatedInsights && generatedInsights.length > 0 ? (
        <div className="su-section">
          <div className="su-insights-generated-header">
            <div className="su-insights-generated-title">
              Generated Insights
            </div>
            <button
              type="button"
              onClick={resetForm}
              className="su-btn-primary"
            >
              <Sparkles className="w-4 h-4 mr-2" /> Generate New
            </button>
          </div>

          <div className="su-insights-grid">
            {generatedInsights.map((insight) => {
              const isSelected = selected.has(insight.id);
              const providers = (
                Array.isArray(insight.data_sources)
                  ? insight.data_sources
                  : insight.data_sources
                  ? [insight.data_sources]
                  : []
              )
                .map((srcId) =>
                  integrations.find((it) => it.id === String(srcId))
                )
                .map((int) =>
                  (int?.provider || "")
                    .toLowerCase()
                    .replace(/\s+/g, "-")
                    .replace(/_/g, "-")
                );
              return (
                <InsightListCard
                  key={insight.id}
                  insight={insight}
                  isSelected={isSelected}
                  onToggle={toggle}
                  providers={providers}
                />
              );
            })}
          </div>
        </div>
      ) : (
        <>
          {genError && (
            <div className="su-insights-error">
              {genError}
            </div>
          )}
            <div className="su-section">
              <div className="su-insights-section-title">
                Your Question or Goal <span style={{ fontWeight: 400, color: 'var(--su-text-muted)' }}>(optional)</span>
              </div>
              <textarea
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="e.g., Compare this week's signups vs last week across GitHub and Stripe"
                className="su-insights-question-input"
              />
            </div>

            <div className="su-section">
              <div className="flex items-center justify-between mb-4">
                <div className="su-insights-section-title" style={{ marginBottom: 0 }}>
                  Select Data Sources
                </div>
                <button
                  onClick={() => navigate("/integrations")}
                  className="text-sm text-purple-400 hover:underline"
                >
                  Manage Integrations
                </button>
              </div>

              <div className="su-insights-integrations-grid">
                {loadingIntegrations ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="h-24 su-insights-skeleton" />
                  ))
                ) : (
                  <>
                    {integrations.map((int) => {
                      const isSelected = selectedIntegrationIds.includes(int.id);
                      const iconSrc = getIntegrationIconSrc(int.provider || "");
                      const providerKey = normalizeProviderKey(int.provider || "");
                      return (
                        <button
                          key={int.id}
                          type="button"
                          onClick={() => toggleIntegration(int.id)}
                          className={cn(
                            "su-insights-integration-card",
                            isSelected && "selected"
                          )}
                          style={{ position: 'relative' }}
                        >
                          {isSelected && (
                            <div className="su-insights-integration-check">
                              <Check className="w-3 h-3" />
                            </div>
                          )}
                          <div className="su-insights-integration-icon">
                            {iconSrc ? (
                              <img
                                src={iconSrc}
                                alt={getIntegrationLabel(int.provider)}
                                className={cn(
                                  providerKey === "gitlab"
                                    ? "max-w-[38px] max-h-[38px]"
                                    : "max-w-[28px] max-h-[28px]",
                                  "object-contain"
                                )}
                              />
                            ) : (
                              <Sparkles className="w-7 h-7 text-gray-100" />
                            )}
                          </div>
                          <div className="su-insights-integration-name">
                            {getIntegrationLabel(int.provider)}
                          </div>
                        </button>
                      );
                    })}

                    <button
                      type="button"
                      onClick={() => navigate("/integrations")}
                      className="su-insights-integration-card"
                      style={{ borderStyle: 'dashed' }}
                    >
                      <div className="su-insights-integration-fallback">
                        <Plus className="w-4 h-4" />
                      </div>
                      <div className="su-insights-integration-name">
                        Add New
                      </div>
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Expanded selection UI per selected integration */}
            {selectedIntegrationIds.length > 0 && (
              <div className="space-y-4">
                <label className="text-sm font-medium text-gray-300">
                  Selected Data
                </label>
                <div className="space-y-3">
                  {selectedIntegrationIds.map((intId) => {
                    const integration = integrations.find(
                      (i) => i.id === intId
                    );
                    if (!integration) return null;
                    const providerKey = (
                      integration.provider || ""
                    ).toLowerCase();
                    const opts = optionsByProvider[providerKey];
                    const sel = selectionByIntegrationId[intId] || {
                      selectedIds: {},
                      kinds: {},
                    };
                    const updateSel = (next: typeof sel) =>
                      setSelectionByIntegrationId((prev) => ({
                        ...prev,
                        [intId]: next,
                      }));

                    return (
                      <div
                        key={intId}
                        className="border border-gray-800 rounded-none p-3 bg-black"
                      >
                        <div className="flex items-center justify-between">
                          <div className="text-sm font-medium text-gray-100">
                            {getIntegrationLabel(integration.provider)}
                          </div>
                          <div className="text-xs text-gray-400">
                            {opts?.type === "list"
                              ? `Select ${opts.label} and kinds`
                              : `Select kinds`}
                          </div>
                        </div>

                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                          {/* Item list */}
                          {opts && (opts as any).type === "list" && (
                            <div className="border border-gray-800 rounded-none p-2 max-h-48 overflow-auto bg-n-2">
                              <div className="text-xs font-medium text-gray-300 mb-1">
                                {(opts as any).label}
                              </div>
                              <ul className="divide-y divide-gray-800">
                                {((opts as any).items || []).map((it: any) => (
                                  <li
                                    key={it.id}
                                    className="flex items-center gap-2 p-2"
                                  >
                                    <label className="inline-flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        className="peer absolute opacity-0 w-0 h-0"
                                        checked={!!sel.selectedIds[it.id]}
                                        onChange={(e) =>
                                          updateSel({
                                            ...sel,
                                            selectedIds: {
                                              ...sel.selectedIds,
                                              [it.id]: e.target.checked,
                                            },
                                          })
                                        }
                                      />
                                      <span
                                        className={cn(
                                          "inline-block h-4 w-4 rounded-none border-2 border-gray-600 bg-black relative",
                                          // White checkmark when checked
                                          "after:hidden peer-checked:after:block after:content-[''] after:absolute after:w-2 after:h-1 after:border-b-2 after:border-l-2 after:border-white after:rotate-[-45deg] after:left-1 after:top-[3px]",
                                          // Purple when checked
                                          "peer-checked:!border-purple-500 peer-checked:!bg-purple-600"
                                        )}
                                      />
                                      <span className="text-sm text-gray-200">
                                        {it.name}
                                      </span>
                                    </label>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Kinds */}
                          <div className="border border-gray-800 rounded-none p-2 max-h-48 overflow-auto bg-n-2">
                            <div className="text-xs font-medium text-gray-300 mb-1">
                              Data kinds
                            </div>
                            <ul className="divide-y divide-gray-800">
                              {((opts as any)?.kinds || []).map((k: any) => (
                                <li
                                  key={k.id}
                                  className="flex items-center gap-2 p-2"
                                >
                                  <label className="inline-flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      className="peer absolute opacity-0 w-0 h-0"
                                      checked={!!sel.kinds[k.id]}
                                      onChange={(e) =>
                                        updateSel({
                                          ...sel,
                                          kinds: {
                                            ...sel.kinds,
                                            [k.id]: e.target.checked,
                                          },
                                        })
                                      }
                                    />
                                    <span
                                      className={cn(
                                        "inline-block h-4 w-4 rounded-none border-2 border-gray-600 bg-black relative",
                                        // White checkmark when checked
                                        "after:hidden peer-checked:after:block after:content-[''] after:absolute after:w-2 after:h-1 after:border-b-2 after:border-l-2 after:border-white after:rotate-[-45deg] after:left-1 after:top-[3px]",
                                        // Purple when checked
                                        "peer-checked:!border-purple-500 peer-checked:!bg-purple-600"
                                      )}
                                    />
                                    <span className="text-sm text-gray-200">
                                      {k.name}
                                    </span>
                                    {k.description && (
                                      <span className="ml-2 text-xs text-gray-400">
                                        {k.description}
                                      </span>
                                    )}
                                  </label>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="su-section">
              <div className="su-insights-section-title">Time Range</div>
              <div className="su-insights-date-presets">
                <button
                  type="button"
                  onClick={() => quickSelect("last_week")}
                  className={cn("su-insights-date-preset", preset === "last_week" && "active")}
                >
                  Last Week
                </button>
                <button
                  type="button"
                  onClick={() => quickSelect("last_month")}
                  className={cn("su-insights-date-preset", preset === "last_month" && "active")}
                >
                  Last Month
                </button>
                <button
                  type="button"
                  onClick={() => quickSelect("last_quarter")}
                  className={cn("su-insights-date-preset", preset === "last_quarter" && "active")}
                >
                  Last Quarter
                </button>
                <button
                  type="button"
                  onClick={() => quickSelect("custom")}
                  className={cn("su-insights-date-preset", preset === "custom" && "active")}
                >
                  Custom
                </button>
              </div>
              <div className="su-insights-date-inputs">
                <div className="relative flex-1">
                  <CalendarIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    type="date"
                    value={dateStart}
                    onChange={(e) => {
                      setDateStart(e.target.value);
                      setPreset("custom");
                    }}
                    className="su-insights-date-input"
                    style={{ paddingLeft: '2.5rem' }}
                  />
                </div>
                <span className="su-insights-date-separator">to</span>
                <div className="relative flex-1">
                  <CalendarIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    type="date"
                    value={dateEnd}
                    onChange={(e) => {
                      setDateEnd(e.target.value);
                      setPreset("custom");
                    }}
                    className="su-insights-date-input"
                    style={{ paddingLeft: '2.5rem' }}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-center pt-2">
              <button
                type="button"
                disabled={isGenerating || selectedIntegrationIds.length === 0}
                onClick={handleGenerate}
                className="su-insights-generate-btn"
              >
                <Sparkles className="w-4 h-4" />
                {isGenerating ? "Generating Insights" : "Generate Insights"}
              </button>
            </div>
          </>
        )}
      
      {generatedInsights && selected.size > 0 && (
        <div className="su-insights-selection-bar">
          <div className="su-insights-selection-bar-inner">
            <div className="su-insights-selection-count">
              {selected.size} insight{selected.size !== 1 ? "s" : ""} selected
            </div>
            <div className="su-insights-selection-actions">
              <button
                type="button"
                onClick={() => setSelected(new Set())}
                className="su-btn-secondary"
              >
                Clear Selection
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!onCreateUpdate || !generatedInsights) return;
                  const selectedInsights = generatedInsights.filter((i) =>
                    selected.has(i.id)
                  );
                  onCreateUpdate(selectedInsights);
                  // Clear selection to hide overlay when modal opens
                  setSelected(new Set());
                }}
                className="su-btn-primary"
              >
                Create Update
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// QuickRangeButton removed - using inline buttons with su-insights-date-preset class

const QuickRangeButtonLegacy: React.FC<{
  label: string;
  active?: boolean;
  onClick: () => void;
}> = ({ label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "px-3 py-1.5 text-sm rounded-none border",
      active
        ? "bg-black text-white border-2 !border-purple-500"
        : "border-gray-700 text-gray-300 hover:bg-gray-800"
    )}
  >
    {label}
  </button>
);

// --- Automated Insights ---
const AutomatedInsights: React.FC<{
  onCreateUpdate: (insights: Insight[]) => void;
}> = ({ onCreateUpdate }) => {
  const { search, isSearching, searchResults } = useInsightSearch();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<string>>(
    new Set()
  );
  const [idToProvider, setIdToProvider] = useState<Record<string, string>>({});

  useEffect(() => {
    const end = new Date();
    const start = new Date(end.getTime() - 7 * 24 * 60 * 60 * 1000);
    search({
      dateRange: { start: start.toISOString(), end: end.toISOString() },
      limit: 50,
      sortBy: "updated_at",
      sortOrder: "desc",
    }).catch((err) => {
      console.error("AutomatedInsights search failed", err);
    });
  }, [search]);

  // Load integrations to resolve IDs -> providers
  useEffect(() => {
    integrationsService
      .getUserIntegrations()
      .then((list) => {
        const map: Record<string, string> = {};
        list.forEach((i) => {
          map[i.id] = (i.provider || "").toLowerCase();
        });
        setIdToProvider(map);
      })
      .catch((err) => console.warn("Failed to load user integrations", err));
  }, []);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const insights = useMemo(
    () => searchResults?.insights ?? [],
    [searchResults]
  );
  const selectedInsights = useMemo(
    () => insights.filter((i) => selected.has(i.id)),
    [insights, selected]
  );

  const getIntegrationIconSrc = (provider: string): string | null => {
    const map: Record<string, string> = {
      slack: "/assets/integrations/slack.svg",
      github: "/assets/integrations/github-white.svg",
      jira: "/assets/integrations/jira.svg",
      asana: "/assets/integrations/asana.png",
      trello: "/assets/integrations/trello.svg",
      notion: "/assets/integrations/notion.png",
      mixpanel: "/assets/integrations/mixpanel.svg",
      monday: "/assets/integrations/monday.svg",
      clickup: "/assets/integrations/clickup.svg",
      "google-sheets": "/assets/integrations/sheets.png",
      sheets: "/assets/integrations/sheets.png",
      drive: "/assets/integrations/drive.png",
      docs: "/assets/integrations/docs.png",
      stripe: "/assets/integrations/stripe.png",
      hubspot: "/assets/integrations/hubspot.svg",
      intercom: "/assets/integrations/intercom.svg",
      salesforce: "/assets/integrations/salesforce.svg",
      linear: "/assets/integrations/linear.png",
      zendesk: "/assets/integrations/zendesk.svg",
      typeform: "/assets/integrations/typeform.svg",
      twitter: "/assets/integrations/twitter.svg",
      google_contacts: "/assets/integrations/google-contacts.svg",
    };
    return map[provider] || null;
  };

  const normalizeProviderKey = (raw: string): string => {
    const key = (raw || "").toLowerCase();
    const aliases: Record<string, string> = {
      gsheets: "google-sheets",
      "google sheets": "google-sheets",
      "google-sheets": "google-sheets",
      "g sheets": "google-sheets",
      ga: "google_analytics",
      "google-analytics": "google_analytics",
      sheets: "google-sheets",
    };
    return aliases[key] || key.replace(/\s+/g, "_");
  };

  const resolveProvider = (raw: string): string => {
    const idResolved = idToProvider[raw];
    if (idResolved) return normalizeProviderKey(idResolved);
    return normalizeProviderKey(raw);
  };

  const formatMetricValue = (dp?: any): string | null => {
    if (!dp || typeof dp.value !== "number") return null;
    const display = dp.display || {};
    const prefix = display.prefix || (dp.format === "currency" ? "$" : "");
    const precision = display.precision ?? (dp.format === "percentage" ? 0 : 0);
    // do not append suffix here; units are rendered separately
    if (dp.format === "percentage") return `${dp.value.toFixed(precision)}%`;
    return `${prefix}${dp.value.toFixed(precision)}`;
  };

  const formatDeltaBadge = (dp?: any): { text: string; cls: string } | null => {
    const pct = typeof dp?.delta_pct === "number" ? dp.delta_pct : undefined;
    if (pct === undefined) return null;
    const isUp = pct >= 0;
    const text = `${isUp ? "+" : ""}${Math.round(pct * 100)}%`;
    const cls = isUp
      ? "bg-green-900 text-green-200 border border-green-600"
      : "bg-red-900 text-red-200 border border-red-600";
    return { text, cls };
  };

  const cleanLabel = (label?: string): string | null => {
    if (!label) return null;
    // remove noise words like avg/average/median/mean
    const t = label
      .toLowerCase()
      .replace(/\b(avg|average|median|mean)\b/g, "")
      .replace(/\s+/g, " ")
      .trim();
    return t || null;
  };

  const computeValueAndUnit = (
    dp?: any
  ): { text: string | null; unit: string | null } => {
    if (!dp || typeof dp.value !== "number") return { text: null, unit: null };
    const display = dp.display || {};
    const precision = display.precision ?? (dp.format === "percentage" ? 0 : 0);

    if (dp.format === "percentage") {
      const text = `${dp.value.toFixed(precision)}%`;
      const unit = cleanLabel(dp.label);
      return { text, unit };
    }

    if (dp.format === "duration") {
      const explicit = (display.suffix || dp?.metadata?.unit || "") as string;
      const unitWord = /[a-zA-Z]/.test(explicit)
        ? explicit.toLowerCase()
        : "hours";
      const text = `${dp.value.toFixed(precision)} ${unitWord}`;
      return { text, unit: null };
    }

    // currency/number
    const text = formatMetricValue(dp);
    const unit = cleanLabel(dp.label);
    return { text, unit };
  };

  return (
    <div className="su-section">
      <div className="su-insights-section-title">Automated Insights</div>
      <div className="su-insights-grid">
        {isSearching &&
          insights.length === 0 &&
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-40 su-insights-skeleton" />
          ))}
        {insights.map((insight) => {
          const dp = insight.primary_dataset?.data_points?.[0] as any;
          const { text: valueText, unit: valueUnit } = computeValueAndUnit(dp);
          const delta = formatDeltaBadge(dp);
          const providers = (insight.data_sources || []).map((p) =>
            resolveProvider(String(p))
          );
          const isExpanded = expandedDescriptions.has(insight.id);
          return (
            <button
              key={insight.id}
              type="button"
              onClick={() => toggle(insight.id)}
              className={cn(
                "su-insights-card",
                selected.has(insight.id) && "selected"
              )}
            >
              <div
                className={cn(
                  "su-insights-card-checkbox",
                  selected.has(insight.id) && "checked"
                )}
              >
                {selected.has(insight.id) && (
                  <Check className="w-4 h-4 text-white" />
                )}
              </div>

              {/* Integrations row with icons + names */}
              <div className="su-insights-card-providers">
                {providers.slice(0, 3).map((p, idx) => {
                  const provider = normalizeProviderKey(String(p));
                  const src = getIntegrationIconSrc(provider);
                  const label = getIntegrationLabel(provider);
                  return (
                    <span
                      key={`${provider}-${idx}`}
                      className="su-insights-card-provider"
                    >
                      {src ? (
                        <img
                          src={src}
                          alt={label}
                          className="w-4 h-4 object-contain"
                        />
                      ) : (
                        <span className="w-4 h-4 bg-gray-800 text-gray-400 text-[10px] flex items-center justify-center border border-gray-700">
                          {label.slice(0, 2).toUpperCase()}
                        </span>
                      )}
                      <span className="max-w-[90px] truncate">{label}</span>
                    </span>
                  );
                })}
                {providers.length > 3 && (
                  <span className="text-xs text-gray-500">
                    +{providers.length - 3} more
                  </span>
                )}
              </div>

              {/* Title / headline */}
              <div className="su-insights-card-title">
                {insight.summary?.headline || insight.title}
              </div>

              {/* Metric row */}
              <div>
                {/* Meta row */}
                <div className="su-insights-card-meta">
                  {insight.time_range_start && insight.time_range_end && (
                    <span>
                      {formatDateRange(insight.time_range_start, insight.time_range_end)}
                    </span>
                  )}
                </div>
                {valueText && (
                  <div className="flex items-baseline">
                    <span className="su-insights-card-value">{valueText}</span>
                    {valueUnit && (
                      <span className="su-insights-card-unit">{valueUnit}</span>
                    )}
                  </div>
                )}
                {delta && (
                  <div>
                    <span
                      className={cn(
                        "su-insights-card-delta",
                        delta.cls.includes("green") ? "positive" : "negative"
                      )}
                    >
                      {delta.text}
                    </span>
                  </div>
                )}
              </div>

              {/* Description */}
              {insight.summary?.narrative && (
                <div className="su-insights-card-narrative">
                  <span className={cn(!isExpanded && "line-clamp-2")}>
                    {insight.summary.narrative}
                  </span>
                  <button
                    type="button"
                    className="su-insights-card-more"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedDescriptions((prev) => {
                        const next = new Set(prev);
                        if (next.has(insight.id)) next.delete(insight.id);
                        else next.add(insight.id);
                        return next;
                      });
                    }}
                  >
                    {isExpanded ? "less" : "more"}
                  </button>
                </div>
              )}
              {insight.summary?.key_findings &&
                Array.isArray(insight.summary.key_findings) &&
                insight.summary.key_findings.length > 0 && (
                  <div className="su-insights-card-findings">
                    <div className="su-insights-card-findings-title">
                      Findings
                    </div>
                    <ul className="su-insights-card-findings-list">
                      {insight.summary.key_findings.slice(0, 3).map((k, i) => (
                        <li key={i}>{String(k)}</li>
                      ))}
                    </ul>
                  </div>
                )}
              {insight.summary?.recommendations &&
                Array.isArray(insight.summary.recommendations) &&
                insight.summary.recommendations.length > 0 && (
                  <div className="su-insights-card-findings">
                    <div className="su-insights-card-findings-title">
                      Recommendations
                    </div>
                    <ul className="su-insights-card-findings-list">
                      {insight.summary.recommendations
                        .slice(0, 3)
                        .map((r, i) => (
                          <li key={i}>{String(r)}</li>
                        ))}
                    </ul>
                  </div>
                )}
            </button>
          );
        })}
      </div>

      {insights.length === 0 && !isSearching && (
        <div className="su-insights-empty">No automated insights found.</div>
      )}

      {selected.size > 0 && (
        <div className="su-insights-selection-bar">
          <div className="su-insights-selection-bar-inner">
            <div className="su-insights-selection-count">
              {selected.size} insight{selected.size !== 1 ? "s" : ""} selected
            </div>
            <div className="su-insights-selection-actions">
              <button
                type="button"
                onClick={() => setSelected(new Set())}
                className="su-btn-secondary"
              >
                Clear Selection
              </button>
              <button
                type="button"
                onClick={() => {
                  onCreateUpdate(selectedInsights);
                  setSelected(new Set());
                }}
                className="su-btn-primary"
              >
                Create Update
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- Generation History ---
interface GenerationHistoryProps {
  onCloneInsight?: (item: GenerationHistoryItem) => void;
}

const GenerationHistory: React.FC<GenerationHistoryProps> = ({
  onCloneInsight,
}) => {
  const navigate = useNavigate();
  const {
    items,
    total,
    hasMore,
    isLoading,
    isLoadingMore,
    error,
    filters,
    setFilters,
    refresh,
    loadMore,
    deleteItem,
    getItem,
  } = useGenerationHistory(20);

  const [searchInput, setSearchInput] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedItem, setSelectedItem] = useState<GenerationHistoryItem | null>(
    null
  );
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [integrations, setIntegrations] = useState<UserIntegration[]>([]);

  // Load integrations for displaying icons
  useEffect(() => {
    integrationsService
      .getUserIntegrations()
      .then((list) => {
        setIntegrations(list.filter((i) => i.integration_status === "active"));
      })
      .catch((err) => console.warn("Failed to load integrations", err));
  }, []);

  // Debounced search
  useEffect(() => {
    const timeout = setTimeout(() => {
      setFilters({
        ...filters,
        searchQuery: searchInput || undefined,
      });
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  const handleDatePreset = (preset: "week" | "month" | "quarter" | "all") => {
    const end = new Date();
    let start = new Date();

    if (preset === "week") {
      start = new Date(end.getTime() - 7 * 24 * 60 * 60 * 1000);
    } else if (preset === "month") {
      start = new Date(end.getTime() - 30 * 24 * 60 * 60 * 1000);
    } else if (preset === "quarter") {
      start = new Date(end.getTime() - 90 * 24 * 60 * 60 * 1000);
    } else {
      setFilters({ ...filters, dateRange: undefined });
      return;
    }

    setFilters({
      ...filters,
      dateRange: {
        start: start.toISOString(),
        end: end.toISOString(),
      },
    });
  };

  const handleTypeFilter = (type: GenerationType | "all") => {
    if (type === "all") {
      setFilters({ ...filters, type: undefined });
    } else {
      setFilters({ ...filters, type: [type] });
    }
  };

  const handleStatusFilter = (status: GenerationStatus | "all") => {
    if (status === "all") {
      setFilters({ ...filters, status: undefined });
    } else {
      setFilters({ ...filters, status: [status] });
    }
  };

  const clearFilters = () => {
    setSearchInput("");
    setFilters({});
  };

  const handleViewItem = async (item: GenerationHistoryItem) => {
    const fullItem = await getItem(item.id);
    if (fullItem) {
      setSelectedItem(fullItem);
      setShowDetailModal(true);
    }
  };

  const handleDeleteItem = async (item: GenerationHistoryItem) => {
    if (!confirm("Are you sure you want to delete this item?")) return;

    setDeletingId(item.id);
    const success = await deleteItem(item.id, item.type);
    setDeletingId(null);

    if (!success) {
      alert("Failed to delete item. Please try again.");
    }
  };

  const handleCloneItem = (item: GenerationHistoryItem) => {
    if (item.type === "insight" && onCloneInsight) {
      onCloneInsight(item);
    } else if (item.type === "email") {
      // Navigate to compose with email content
      const composeState = {
        clonedEmail: {
          subject: item.metadata.emailSubject,
          body: item.fullContent?.emailBody,
        },
      };
      localStorage.setItem("compose_temp_state", JSON.stringify(composeState));
      navigate("/updates/new");
    }
  };

  const getStatusBadge = (status: GenerationStatus) => {
    const config: Record<
      GenerationStatus,
      { label: string; className: string; icon: React.ReactNode }
    > = {
      completed: {
        label: "Completed",
        className: "su-history-status-completed",
        icon: <CheckCircle className="w-3 h-3" />,
      },
      sent: {
        label: "Sent",
        className: "su-history-status-sent",
        icon: <Send className="w-3 h-3" />,
      },
      draft: {
        label: "Draft",
        className: "su-history-status-draft",
        icon: <FileEdit className="w-3 h-3" />,
      },
      failed: {
        label: "Failed",
        className: "su-history-status-failed",
        icon: <AlertCircle className="w-3 h-3" />,
      },
      pending: {
        label: "Pending",
        className: "su-history-status-pending",
        icon: <Clock className="w-3 h-3" />,
      },
      in_progress: {
        label: "In Progress",
        className: "su-history-status-pending",
        icon: <Loader2 className="w-3 h-3 animate-spin" />,
      },
    };

    const { label, className, icon } = config[status] || config.completed;

    return (
      <span className={cn("su-history-status-badge", className)}>
        {icon}
        {label}
      </span>
    );
  };

  const getTypeBadge = (type: GenerationType) => {
    if (type === "insight") {
      return (
        <span className="su-history-type-badge su-history-type-insight">
          <Lightbulb className="w-3 h-3" />
          Insight
        </span>
      );
    }
    return (
      <span className="su-history-type-badge su-history-type-email">
        <Mail className="w-3 h-3" />
        Email
      </span>
    );
  };

  const getIntegrationIcon = (intId: string): string | null => {
    const integration = integrations.find((i) => i.id === intId);
    if (!integration) return null;

    const provider = (integration.provider || "").toLowerCase();
    const iconMap: Record<string, string> = {
      slack: "/assets/integrations/slack.svg",
      github: "/assets/integrations/github-white.svg",
      jira: "/assets/integrations/jira.svg",
      "google-sheets": "/assets/integrations/sheets.png",
      stripe: "/assets/integrations/stripe.png",
      hubspot: "/assets/integrations/hubspot.svg",
      linear: "/assets/integrations/linear-white.png",
      notion: "/assets/integrations/notion.png",
    };
    return iconMap[provider] || null;
  };

  const hasActiveFilters =
    filters.searchQuery ||
    filters.dateRange ||
    filters.type ||
    filters.status;

  return (
    <div className="su-section">
      {/* Header with search and filters */}
      <div className="su-history-header">
        <div className="su-history-header-top">
          <div className="su-insights-section-title" style={{ marginBottom: 0 }}>
            Generation History
          </div>
          <div className="su-history-header-actions">
            <button
              type="button"
              onClick={refresh}
              className="su-history-refresh-btn"
              disabled={isLoading}
            >
              <RefreshCw
                className={cn("w-4 h-4", isLoading && "animate-spin")}
              />
            </button>
          </div>
        </div>

        {/* Search bar */}
        <div className="su-history-search-bar">
          <div className="su-history-search-input-wrapper">
            <Search className="w-4 h-4 su-history-search-icon" />
            <input
              type="text"
              placeholder="Search by title, content, or subject..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="su-history-search-input"
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => setSearchInput("")}
                className="su-history-search-clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={cn(
              "su-history-filter-toggle",
              showFilters && "active",
              hasActiveFilters && "has-filters"
            )}
          >
            <Filter className="w-4 h-4" />
            Filters
            {hasActiveFilters && (
              <span className="su-history-filter-count">
                {
                  [
                    filters.dateRange,
                    filters.type,
                    filters.status,
                  ].filter(Boolean).length
                }
              </span>
            )}
          </button>
        </div>

        {/* Filter panel */}
        {showFilters && (
          <div className="su-history-filters">
            <div className="su-history-filter-group">
              <div className="su-history-filter-label">Date Range</div>
              <div className="su-history-filter-options">
                <button
                  type="button"
                  onClick={() => handleDatePreset("all")}
                  className={cn(
                    "su-history-filter-btn",
                    !filters.dateRange && "active"
                  )}
                >
                  All Time
                </button>
                <button
                  type="button"
                  onClick={() => handleDatePreset("week")}
                  className={cn(
                    "su-history-filter-btn",
                    filters.dateRange &&
                      new Date(filters.dateRange.start).getTime() >
                        Date.now() - 8 * 24 * 60 * 60 * 1000 &&
                      "active"
                  )}
                >
                  Last 7 Days
                </button>
                <button
                  type="button"
                  onClick={() => handleDatePreset("month")}
                  className="su-history-filter-btn"
                >
                  Last 30 Days
                </button>
                <button
                  type="button"
                  onClick={() => handleDatePreset("quarter")}
                  className="su-history-filter-btn"
                >
                  Last 90 Days
                </button>
              </div>
            </div>

            <div className="su-history-filter-group">
              <div className="su-history-filter-label">Type</div>
              <div className="su-history-filter-options">
                <button
                  type="button"
                  onClick={() => handleTypeFilter("all")}
                  className={cn(
                    "su-history-filter-btn",
                    !filters.type && "active"
                  )}
                >
                  All
                </button>
                <button
                  type="button"
                  onClick={() => handleTypeFilter("insight")}
                  className={cn(
                    "su-history-filter-btn",
                    filters.type?.includes("insight") && "active"
                  )}
                >
                  <Lightbulb className="w-3 h-3" />
                  Insights
                </button>
                <button
                  type="button"
                  onClick={() => handleTypeFilter("email")}
                  className={cn(
                    "su-history-filter-btn",
                    filters.type?.includes("email") && "active"
                  )}
                >
                  <Mail className="w-3 h-3" />
                  Emails
                </button>
              </div>
            </div>

            <div className="su-history-filter-group">
              <div className="su-history-filter-label">Status</div>
              <div className="su-history-filter-options">
                <button
                  type="button"
                  onClick={() => handleStatusFilter("all")}
                  className={cn(
                    "su-history-filter-btn",
                    !filters.status && "active"
                  )}
                >
                  All
                </button>
                <button
                  type="button"
                  onClick={() => handleStatusFilter("completed")}
                  className={cn(
                    "su-history-filter-btn",
                    filters.status?.includes("completed") && "active"
                  )}
                >
                  Completed
                </button>
                <button
                  type="button"
                  onClick={() => handleStatusFilter("sent")}
                  className={cn(
                    "su-history-filter-btn",
                    filters.status?.includes("sent") && "active"
                  )}
                >
                  Sent
                </button>
                <button
                  type="button"
                  onClick={() => handleStatusFilter("failed")}
                  className={cn(
                    "su-history-filter-btn",
                    filters.status?.includes("failed") && "active"
                  )}
                >
                  Failed
                </button>
              </div>
            </div>

            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="su-history-clear-filters"
              >
                <X className="w-3 h-3" />
                Clear All Filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Results count */}
      {!isLoading && (
        <div className="su-history-results-count">
          Showing {items.length} of {total} items
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="su-insights-error">
          {error}
          <button type="button" onClick={refresh} className="ml-2 underline">
            Retry
          </button>
        </div>
      )}

      {/* Loading state */}
      {isLoading && items.length === 0 && (
        <div className="su-insights-history-list">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="su-history-item-skeleton">
              <div className="su-history-skeleton-badges">
                <div className="su-history-skeleton-badge" />
                <div className="su-history-skeleton-badge" />
              </div>
              <div className="su-history-skeleton-title" />
              <div className="su-history-skeleton-preview" />
            </div>
          ))}
        </div>
      )}

      {/* History list */}
      {!isLoading && items.length > 0 && (
        <div className="su-insights-history-list">
          {items.map((item) => (
            <div
              key={item.id}
              className="su-history-item"
              onClick={() => handleViewItem(item)}
            >
              <div className="su-history-item-header">
                <div className="su-history-item-badges">
                  {getTypeBadge(item.type)}
                  {getStatusBadge(item.status)}
                </div>
                <div className="su-history-item-date">
                  {formatDateTime(item.createdAt)}
                </div>
              </div>

              <div className="su-history-item-title">{item.title}</div>

              <div className="su-history-item-preview">{item.preview}</div>

              {/* Data sources */}
              {item.dataSources.length > 0 && (
                <div className="su-history-item-sources">
                  {item.dataSources.slice(0, 3).map((ds, idx) => {
                    const iconSrc = getIntegrationIcon(ds.id);
                    return (
                      <span key={idx} className="su-history-item-source">
                        {iconSrc ? (
                          <img
                            src={iconSrc}
                            alt={ds.type}
                            className="w-3 h-3 object-contain"
                          />
                        ) : (
                          <span className="su-history-source-fallback">
                            {ds.type.slice(0, 2).toUpperCase()}
                          </span>
                        )}
                      </span>
                    );
                  })}
                  {item.dataSources.length > 3 && (
                    <span className="su-history-item-source-more">
                      +{item.dataSources.length - 3}
                    </span>
                  )}
                </div>
              )}

              {/* Metadata row for emails */}
              {item.type === "email" && (
                <div className="su-history-item-meta">
                  {item.metadata.recipientCount !== undefined && (
                    <span>
                      {item.metadata.recipientCount} recipient
                      {item.metadata.recipientCount !== 1 ? "s" : ""}
                    </span>
                  )}
                  {item.metadata.successCount !== undefined && (
                    <span className="su-history-meta-success">
                      {item.metadata.successCount} delivered
                    </span>
                  )}
                  {item.metadata.openCount !== undefined &&
                    item.metadata.openCount > 0 && (
                      <span className="su-history-meta-opens">
                        {item.metadata.openCount} opened
                      </span>
                    )}
                </div>
              )}

              {/* Action buttons */}
              <div className="su-history-item-actions">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewItem(item);
                  }}
                  className="su-history-action-btn"
                  title="View Details"
                >
                  <Eye className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCloneItem(item);
                  }}
                  className="su-history-action-btn"
                  title="Clone / Use as Template"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteItem(item);
                  }}
                  className="su-history-action-btn su-history-action-delete"
                  title="Delete"
                  disabled={deletingId === item.id}
                >
                  {deletingId === item.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && items.length === 0 && !error && (
        <div className="su-history-empty">
          <div className="su-history-empty-icon">
            <Clock className="w-12 h-12" />
          </div>
          <div className="su-history-empty-title">No generation history yet</div>
          <div className="su-history-empty-text">
            {hasActiveFilters
              ? "No items match your current filters. Try adjusting your search criteria."
              : "Generate insights or send emails to see them appear here."}
          </div>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="su-btn-secondary mt-4"
            >
              Clear Filters
            </button>
          )}
        </div>
      )}

      {/* Load more button */}
      {hasMore && !isLoading && (
        <div className="su-history-load-more">
          <button
            type="button"
            onClick={loadMore}
            disabled={isLoadingMore}
            className="su-history-load-more-btn"
          >
            {isLoadingMore ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading...
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                Load More
              </>
            )}
          </button>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedItem && (
        <div
          className="su-history-modal-overlay"
          onClick={() => setShowDetailModal(false)}
        >
          <div
            className="su-history-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="su-history-modal-header">
              <div className="su-history-modal-badges">
                {getTypeBadge(selectedItem.type)}
                {getStatusBadge(selectedItem.status)}
              </div>
              <button
                type="button"
                onClick={() => setShowDetailModal(false)}
                className="su-history-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <h2 className="su-history-modal-title">{selectedItem.title}</h2>

            <div className="su-history-modal-meta">
              <span>Created: {formatDateTime(selectedItem.createdAt)}</span>
              {selectedItem.metadata.model && (
                <span>Model: {selectedItem.metadata.model}</span>
              )}
              {selectedItem.metadata.processingTimeMs && (
                <span>
                  Processing: {selectedItem.metadata.processingTimeMs}ms
                </span>
              )}
            </div>

            <div className="su-history-modal-content">
              {selectedItem.type === "insight" && (
                <>
                  {selectedItem.fullContent?.headline && (
                    <div className="su-history-modal-section">
                      <h3>Headline</h3>
                      <p>{selectedItem.fullContent.headline}</p>
                    </div>
                  )}
                  {selectedItem.fullContent?.narrative && (
                    <div className="su-history-modal-section">
                      <h3>Narrative</h3>
                      <p>{selectedItem.fullContent.narrative}</p>
                    </div>
                  )}
                  {selectedItem.fullContent?.keyFindings &&
                    selectedItem.fullContent.keyFindings.length > 0 && (
                      <div className="su-history-modal-section">
                        <h3>Key Findings</h3>
                        <ul>
                          {selectedItem.fullContent.keyFindings.map((f, i) => (
                            <li key={i}>{f}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  {selectedItem.fullContent?.recommendations &&
                    selectedItem.fullContent.recommendations.length > 0 && (
                      <div className="su-history-modal-section">
                        <h3>Recommendations</h3>
                        <ul>
                          {selectedItem.fullContent.recommendations.map(
                            (r, i) => (
                              <li key={i}>{r}</li>
                            )
                          )}
                        </ul>
                      </div>
                    )}
                </>
              )}

              {selectedItem.type === "email" && (
                <>
                  {selectedItem.metadata.emailSubject && (
                    <div className="su-history-modal-section">
                      <h3>Subject</h3>
                      <p>{selectedItem.metadata.emailSubject}</p>
                    </div>
                  )}
                  <div className="su-history-modal-section">
                    <h3>Email Stats</h3>
                    <div className="su-history-modal-stats">
                      <div className="su-history-modal-stat">
                        <span className="su-history-modal-stat-value">
                          {selectedItem.metadata.recipientCount || 0}
                        </span>
                        <span className="su-history-modal-stat-label">
                          Recipients
                        </span>
                      </div>
                      <div className="su-history-modal-stat">
                        <span className="su-history-modal-stat-value">
                          {selectedItem.metadata.successCount || 0}
                        </span>
                        <span className="su-history-modal-stat-label">
                          Delivered
                        </span>
                      </div>
                      <div className="su-history-modal-stat">
                        <span className="su-history-modal-stat-value">
                          {selectedItem.metadata.openCount || 0}
                        </span>
                        <span className="su-history-modal-stat-label">
                          Opened
                        </span>
                      </div>
                    </div>
                  </div>
                  {selectedItem.fullContent?.emailBody && (
                    <div className="su-history-modal-section">
                      <h3>Email Content</h3>
                      <div
                        className="su-history-modal-email-preview"
                        dangerouslySetInnerHTML={{
                          __html: selectedItem.fullContent.emailBody,
                        }}
                      />
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="su-history-modal-actions">
              <button
                type="button"
                onClick={() => handleCloneItem(selectedItem)}
                className="su-btn-primary"
              >
                <Copy className="w-4 h-4" />
                Use as Template
              </button>
              <button
                type="button"
                onClick={() => setShowDetailModal(false)}
                className="su-btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- Tabs wrapper ---
const TabbedInsights: React.FC<{ userId: string }> = ({ userId }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<"generate" | "automated" | "history">(
    "generate"
  );

  // Navigate to compose page with preselected insights
  const handleCreateUpdate = (insights: Insight[]) => {
    // Store selected insights in localStorage for the compose page to pick up
    const composeState = {
      selectedInsights: insights.map((insight) => ({
        id: insight.id,
        title: insight.title,
        summary: insight.summary,
        data_sources: insight.data_sources,
        created_at: insight.created_at,
      })),
    };
    localStorage.setItem("compose_temp_state", JSON.stringify(composeState));
    navigate("/updates/new");
  };

  return (
    <div className="w-full px-9 py-6">
      <PageHeader
        title="Insights"
        subtitle="Generate and explore data-driven insights from your integrations"
      />

      <div className="su-insights-page">
        <nav className="su-insights-tabs">
          <button
            type="button"
            onClick={() => setTab("generate")}
            className={cn("su-insights-tab", tab === "generate" && "active")}
          >
            Generate Insights
          </button>
          <button
            type="button"
            onClick={() => setTab("automated")}
            className={cn("su-insights-tab", tab === "automated" && "active")}
          >
            Automated Insights
          </button>
          <button
            type="button"
            onClick={() => setTab("history")}
            className={cn("su-insights-tab", tab === "history" && "active")}
          >
            Generation History
          </button>
        </nav>

        {tab === "generate" && (
          <InlineInsightGenerator
            userId={userId}
            onCreateUpdate={handleCreateUpdate}
          />
        )}

        {tab === "automated" && (
          <AutomatedInsights
            onCreateUpdate={handleCreateUpdate}
          />
        )}

        {tab === "history" && <GenerationHistory />}
      </div>
    </div>
  );
};

// TabButton removed - using inline buttons with su-insights-tab class
